import { FormActionFrameworkStep, registerStep } from '../step';
import { resolveConfigStrings } from '../template';
import { bffWorkflowRunner } from '../bffWorkflowRunner';
export class ExecuteWorkflowStep extends FormActionFrameworkStep {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "type", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'executeWorkflow'
        });
        Object.defineProperty(this, "config", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                workflowId: '',
                output: '',
                stopOnError: true,
            }
        });
    }
    async execute(context) {
        const resolved = resolveConfigStrings(this.config, context);
        const workflowId = (typeof resolved.workflowId === 'string' && resolved.workflowId.trim()) ||
            '';
        if (!workflowId) {
            context.log('[executeWorkflow] missing workflowId — skipped');
            return context;
        }
        // Host identity always comes from context (seeded at FAF init) — not step config.
        const initialData = {
            formId: context.formId ?? null,
            incidentId: context.incidentId ?? null,
            taskId: context.taskId ?? null,
            userId: context.userId ?? null,
            orgId: context.orgId ?? context.organizationId ?? null,
            pageUrl: context.pageUrl ?? null,
            formContent: context.formValues,
            formValues: context.formValues,
            milestone: context.milestone ?? null,
            ...(resolved.initialData && typeof resolved.initialData === 'object'
                ? resolved.initialData
                : {}),
        };
        context.log(`[executeWorkflow] starting workflowId=${workflowId}`);
        try {
            const result = await ExecuteWorkflowStep.workflowRunner(workflowId, initialData);
            context.variables.set('lastWorkflowResult', result);
            context.variables.set(`workflow:${workflowId}`, result);
            const outputName = (typeof resolved.output === 'string' && resolved.output.trim()) || '';
            if (outputName) {
                context.variables.set(outputName, result);
                context.log(`[executeWorkflow] saved result to variable "${outputName}"`);
            }
            context.log(`[executeWorkflow] completed workflowId=${workflowId}`);
        }
        catch (err) {
            const message = err instanceof Error ? err.message : String(err);
            context.log(`[executeWorkflow] error: ${message}`);
            context.variables.set('lastWorkflowError', message);
            if (resolved.stopOnError !== false) {
                context.shouldStop = true;
            }
        }
        return context;
    }
    showStepForm(container, _context, onUpdated) {
        const cfg = this.config;
        container.innerHTML = `
      <div class="mb-3">
        <label class="form-label">Step name</label>
        <input type="text" class="form-control" data-faf="name" value="${escapeAttr(this.name)}" />
      </div>
      <div class="mb-3">
        <label class="form-label">workflowId</label>
        <input type="text" class="form-control" data-faf="workflowId" value="${escapeAttr(cfg.workflowId)}" />
      </div>
      <div class="mb-3">
        <label class="form-label">Output variable</label>
        <input type="text" class="form-control" data-faf="output" value="${escapeAttr(cfg.output)}" placeholder="variable_name" />
        <div class="text-muted small">Workflow result is stored in context.variables under this name (also lastWorkflowResult).</div>
      </div>
      <div class="mb-3 text-muted small">
        formId / incidentId / taskId / pageUrl come from FAF host context ({{context.taskId}}). Supports {{ }} on fields below.
      </div>
    `;
        const sync = () => {
            this.name = inputValue(container, 'name');
            this.config.workflowId = inputValue(container, 'workflowId');
            this.config.output = inputValue(container, 'output');
            // Drop legacy id fields if present in older saved configs.
            delete this.config.incidentId;
            delete this.config.taskId;
            delete this.config.formId;
            delete this.config.userId;
            delete this.config.orgId;
            onUpdated?.();
        };
        container.querySelectorAll('input').forEach((el) => {
            el.addEventListener('change', sync);
            el.addEventListener('blur', sync);
        });
    }
}
/**
 * Calls portal BFF `POST /api/workflows/execute` → server `executeWorkflow`.
 * Override for tests or alternate hosts.
 */
Object.defineProperty(ExecuteWorkflowStep, "workflowRunner", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: bffWorkflowRunner
});
function inputValue(container, key) {
    const el = container.querySelector(`[data-faf="${key}"]`);
    return el?.value?.trim() ?? '';
}
function escapeAttr(value) {
    if (value == null)
        return '';
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;');
}
registerStep('executeWorkflow', ExecuteWorkflowStep);
